import { useState, useEffect } from 'react'
import { Modal } from '../../components/Common/Modal'
import { Button } from '../../components/Common/Button'

const MERGE_FIELDS = [
  { label: 'Full Name', sfpKey: 'full_name', izuluKey: 'full_name' },
  { label: 'Surname', sfpKey: 'surname', izuluKey: 'surname' },
  { label: 'Initials', sfpKey: 'initials', izuluKey: 'initials' },
  { label: 'Preferred Name', sfpKey: 'preferred_name', izuluKey: 'preferred_name' },
  { label: 'Title', sfpKey: 'title', izuluKey: 'title' },
  { label: 'Gender', sfpKey: 'gender', izuluKey: 'gender' },
  { label: 'ID Number', sfpKey: 'id_number', izuluKey: 'id_number', matchKey: true },
  { label: 'Tax Number', sfpKey: 'tax_number', izuluKey: 'tax_number' },
  { label: 'Mobile', sfpKey: 'mobile_number', izuluKey: 'phone' },
  { label: 'Email', sfpKey: 'email_address', izuluKey: 'email', matchKey: true },
  { label: 'Client Status', sfpKey: 'client_status', izuluKey: 'client_status' },
  { label: 'Date of Birth', sfpKey: 'date_of_birth', izuluKey: 'date_of_birth' },
  { label: 'Retirement Date', sfpKey: 'retirement_date', izuluKey: 'retirement_date' },
]

export function IzuluDuplicateModal({ sfpData, candidates, onMerge, onCreateNew, onCancel, sidebarCollapsed }) {
  const [selectedCandidateIdx, setSelectedCandidateIdx] = useState(0)
  const [selectedChoices, setSelectedChoices] = useState({})

  const currentCandidate = candidates[selectedCandidateIdx]

  // Reset choices when candidate changes
  useEffect(() => {
    setSelectedChoices(Object.fromEntries(MERGE_FIELDS.map(f => [f.sfpKey, 'sfp'])))
  }, [selectedCandidateIdx])

  const handleConfirmMerge = () => {
    const mergedData = { ...sfpData }
    MERGE_FIELDS.forEach(field => {
      if (selectedChoices[field.sfpKey] === 'izulu') {
        // Handle field name mapping (e.g., phone → mobile_number, email → email_address)
        if (field.sfpKey === 'mobile_number') {
          mergedData.mobile_number = currentCandidate.phone
        } else if (field.sfpKey === 'email_address') {
          mergedData.email_address = currentCandidate.email
        } else {
          mergedData[field.sfpKey] = currentCandidate[field.izuluKey]
        }
      }
    })
    onMerge(mergedData, currentCandidate.id)
  }

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    return String(value)
  }

  const valuesAreIdentical = (sfpVal, izuluVal) => {
    const sfpStr = String(sfpVal ?? '')
    const izuluStr = String(izuluVal ?? '')
    return sfpStr === izuluStr
  }

  return (
    <Modal isOpen={true} onClose={onCancel} title="Possible Duplicate Found in IZULU" sidebarCollapsed={sidebarCollapsed}>
      <div className="space-y-4 flex flex-col h-full">
        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded">
          <p className="text-sm font-medium">
            ⓘ A profile in IZULU matches on email or ID number. Review and choose how to proceed.
          </p>
        </div>

        {/* Candidate selector (if multiple matches) */}
        {candidates.length > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 font-medium">Reviewing match:</span>
            <select
              className="input-field w-auto"
              value={selectedCandidateIdx}
              onChange={e => setSelectedCandidateIdx(Number(e.target.value))}
            >
              {candidates.map((c, i) => (
                <option key={c.id} value={i}>
                  {c.full_name || c.surname || '(no name)'} — {c.email || c.id_number || 'no contact'}
                </option>
              ))}
            </select>
            <span className="text-gray-400 text-xs">({candidates.length} matches found)</span>
          </div>
        )}

        {/* Already linked warning */}
        {currentCandidate.sfp_client_id && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm">
            <span className="font-medium">⚠ Already linked:</span> This IZULU profile is already linked to SFP client ID{' '}
            <code className="bg-amber-100 px-2 py-1 rounded text-xs">{currentCandidate.sfp_client_id}</code>. Merging will re-link it to
            this new client.
          </div>
        )}

        {/* Merge table */}
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-sfp-darkgray w-32">Field</th>
                <th className="text-left px-4 py-2 font-semibold text-sfp-darkgray flex-1">SFP (New)</th>
                <th className="text-left px-4 py-2 font-semibold text-sfp-darkgray flex-1">IZULU (Existing)</th>
              </tr>
            </thead>
            <tbody>
              {MERGE_FIELDS.map(field => {
                const sfpValue = sfpData[field.sfpKey]
                const izuluValue = currentCandidate[field.izuluKey]
                const identical = valuesAreIdentical(sfpValue, izuluValue)
                const isMatchKey = field.matchKey

                return (
                  <tr
                    key={field.sfpKey}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${isMatchKey ? 'bg-amber-50' : ''}`}
                  >
                    <td className={`px-4 py-3 font-medium text-sfp-darkgray ${isMatchKey ? 'border-l-4 border-amber-300' : ''}`}>
                      {field.label}
                      {isMatchKey && <span className="text-amber-600 text-xs ml-1">[match]</span>}
                    </td>
                    <td className={`px-4 py-3 ${identical ? 'text-gray-400' : ''}`}>
                      <div className="flex items-center gap-2">
                        {!identical && (
                          <input
                            type="radio"
                            name={`merge-field-${field.sfpKey}`}
                            value="sfp"
                            checked={selectedChoices[field.sfpKey] === 'sfp'}
                            onChange={() =>
                              setSelectedChoices(prev => ({
                                ...prev,
                                [field.sfpKey]: 'sfp',
                              }))
                            }
                          />
                        )}
                        <span>{formatValue(sfpValue)}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${identical ? 'text-gray-400' : ''}`}>
                      <div className="flex items-center gap-2">
                        {!identical && (
                          <input
                            type="radio"
                            name={`merge-field-${field.sfpKey}`}
                            value="izulu"
                            checked={selectedChoices[field.sfpKey] === 'izulu'}
                            onChange={() =>
                              setSelectedChoices(prev => ({
                                ...prev,
                                [field.sfpKey]: 'izulu',
                              }))
                            }
                          />
                        )}
                        <span>{formatValue(izuluValue)}</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Help text */}
        <div className="text-xs text-gray-500 px-2">
          Cancel returns to the form without saving. Select field values above, then confirm to link the records.
        </div>

        {/* Footer buttons */}
        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onCancel} type="button">
            Cancel
          </Button>
          <Button variant="secondary" onClick={onCreateNew} type="button">
            Create New in IZULU
          </Button>
          <Button variant="primary" onClick={handleConfirmMerge} type="button">
            Confirm Merge and Link
          </Button>
        </div>
      </div>
    </Modal>
  )
}
